import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Popover,
  Stack,
} from '@mui/material';
import { Bold, Code, Italic, Smile, Strikethrough, Plus, Trash2 } from 'lucide-react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import TemplatePreview from './TemplatePreview';
import { countriesApi } from '../../../api/countries.api';
import { templatesApi } from '../../../api/templates.api';
import { uploadApi } from '../../../api/upload.api';
import { useRef } from 'react';

const defaultCategories = [
  { code: 'MARKETING', name: 'MARKETING' },
  { code: 'UTILITY', name: 'UTILITY' },
  { code: 'AUTHENTICATION', name: 'AUTHENTICATION' },
  { code: 'TRANSACTIONAL', name: 'TRANSACTIONAL' },
];

const defaultLanguages = [];

const TemplateForm = ({
  template,
  onSubmit,
  onCancel,
  onSaveDraft,
  loading = false,
  isEdit = false,
}) => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: template?.name || '',
      category: template?.category || 'MARKETING',
      language: template?.language || 'en_US',
      header: template?.header || '',
      headerType: template?.headerType || 'none',
      headerMediaUrl: template?.headerMediaUrl || '',
      headerHandle: template?.headerHandle || template?.header_handle || '',
      body: template?.body || '',
      footer: template?.footer || '',
      buttons: template?.buttons || [],
    },
  });

  // Reset form when template prop changes (e.g., when editing)
  useEffect(() => {
    if (template) {
      reset({
        name: template.name || '',
        category: template.category || 'MARKETING',
        language: template.language || 'en_US',
        header: template.header || '',
        headerType: template.headerType || 'none',
        headerMediaUrl: template.headerMediaUrl || '',
        headerHandle: template.headerHandle || template.header_handle || '',
        body: template.body || '',
        footer: template.footer || '',
        buttons: template.buttons || [],
      });
    }
  }, [template, reset]);

  const { fields: buttonFields, append: appendButton, remove: removeButton } = useFieldArray({
    control,
    name: 'buttons',
  });

  const headerInputRef = useRef(null);
  const bodyInputRef = useRef(null);
  const footerInputRef = useRef(null);

  const [headerUploadName, setHeaderUploadName] = useState('');
  const [headerUploadError, setHeaderUploadError] = useState('');
  const [headerUploading, setHeaderUploading] = useState(false);
  const [buttonLimitError, setButtonLimitError] = useState('');
  const [variableError, setVariableError] = useState('');
  const [headerFile, setHeaderFile] = useState(null);
  const [headerPreviewUrl, setHeaderPreviewUrl] = useState('');
  const [countryCodes, setCountryCodes] = useState([]);
  const [loadingCountryCodes, setLoadingCountryCodes] = useState(false);
  const [countryCodeError, setCountryCodeError] = useState('');
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const [emojiTarget, setEmojiTarget] = useState('body');
  const [categories, setCategories] = useState(defaultCategories);
  const [languages, setLanguages] = useState(defaultLanguages);
  const [variableSamples, setVariableSamples] = useState({ header: {}, body: {}, footer: {} });
  const [recentVariable, setRecentVariable] = useState(null);
  const formValues = watch();
  const buttonValues = watch('buttons');
  const sampleInputRefs = useRef({});
  const extractVariables = (text) => {
    if (!text) return [];
    const matches = text.match(/\{\{\s*(\d+)\s*\}\}/g) || [];
    return [...new Set(matches.map((m) => m.replace(/[{}\s]/g, '')))];
  };
  const extractRawVariables = (text) => {
    if (!text) return [];
    const matches = text.match(/\{\{\s*([^}]+)\s*\}\}/g) || [];
    return [...new Set(matches.map((m) => m.replace(/[{}]/g, '').trim()))];
  };

  const headerVariables = extractVariables(formValues.header);
  const bodyVariables = extractVariables(formValues.body);
  const footerVariables = extractVariables(formValues.footer);

  const areSectionSamplesEqual = (left = {}, right = {}) => {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) return false;
    return leftKeys.every((key) => (left[key] || '') === (right[key] || ''));
  };

  const headerAcceptMap = {
    image: 'image/*',
    video: 'video/*',
    document: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt',
  };

  const emojiList = [
    '😀', '😁', '😂', '🤣', '😅', '😊', '😍', '😘',
    '😎', '🤔', '🙌', '🙏', '👍', '👎', '🔥', '✨',
    '🎉', '✅', '⚡', '❗', '❓', '❤️', '💡', '📌',
  ];

  const normalizeDialCode = (code) => {
    const value = `${code || ''}`.trim();
    if (!value) return '';
    return value.startsWith('+') ? value : `+${value}`;
  };

  const mapCountryCode = (entry) => {
    const dial = normalizeDialCode(
      entry?.dial_code ||
      entry?.dialCode ||
      entry?.phone_code ||
      entry?.phoneCode ||
      entry?.country_code ||
      entry?.code ||
      entry?.prefix ||
      entry?.callingCode
    );
    const name =
      entry?.name ||
      entry?.country ||
      entry?.countryName ||
      entry?.country_name ||
      entry?.iso2 ||
      entry?.iso ||
      entry?.alpha2 ||
      entry?.alpha_2;
    return {
      label: name ? `${name}${dial ? ` (${dial})` : ''}` : dial || '',
      value: dial || '',
    };
  };

  const loadCountryCodes = async () => {
    if (countryCodes.length || loadingCountryCodes) return;
    setLoadingCountryCodes(true);
    setCountryCodeError('');
    try {
      const response = await countriesApi.getCountryCodes();
      const list = response?.data?.data || response?.data || [];
      const mapped = Array.isArray(list)
        ? list
            .filter(Boolean)
            .map((entry) => mapCountryCode(entry))
            .filter((item) => item && item.value)
        : [];
      setCountryCodes(mapped);
      if (mapped.length === 0) {
        setCountryCodeError('No country codes available.');
      }
    } catch (error) {
      setCountryCodeError('Failed to load country codes.');
      setCountryCodes([]);
    } finally {
      setLoadingCountryCodes(false);
    }
  };

  const loadMeta = async () => {
    try {
      const [catsRes, langsRes] = await Promise.all([
        templatesApi.getCategories(),
        templatesApi.getLanguages(),
      ]);

      const fetchedCategories = catsRes?.data?.data || catsRes?.data || [];
      if (Array.isArray(fetchedCategories) && fetchedCategories.length) {
        const normalized = fetchedCategories
          .filter(Boolean)
          .map((c) => {
            if (typeof c === 'string' || typeof c === 'number') {
              const code = `${c}`.toUpperCase();
              return { code, name: `${c}` };
            }
            const code = ((c && (c.code || c.value || c.id || c.slug || c.name)) || '').toUpperCase();
            const name = c?.name || c?.label || c?.displayName || code;
            if (!code) return null;
            return { code, name };
          })
          .filter(Boolean);
        if (normalized.length) setCategories(normalized);
      }

      const fetchedLanguages = langsRes?.data?.data || langsRes?.data || [];
      if (Array.isArray(fetchedLanguages) && fetchedLanguages.length) {
        const normalizedLangs = fetchedLanguages
          .filter(Boolean)
          .map((lang) => {
            if (typeof lang === 'string' || typeof lang === 'number') {
              const code = `${lang}`;
              return { code, name: code.toUpperCase() };
            }
            return {
              code: lang?.code || lang?.language || lang?.value || lang?.id || lang?.slug,
              name: lang?.name || lang?.label || lang?.language || lang?.code || lang?.value,
            };
          })
          .filter((l) => l.code && l.name);
        if (normalizedLangs.length) setLanguages(normalizedLangs);
      }
    } catch (err) {
      // keep defaults on failure
    }
  };

  useEffect(() => {
    const hasPhoneButton = Array.isArray(buttonValues)
      ? buttonValues.some((btn) => btn?.type === 'PHONE_NUMBER')
      : false;
    if (hasPhoneButton) {
      loadCountryCodes();
    }
  }, [buttonValues]);

  useEffect(() => {
    loadMeta();
  }, []);

  useEffect(() => {
    if (languages.length) {
      const currentLang = (formValues.language || '').toLowerCase();
      const langCodes = languages
        .filter((l) => l && l.code)
        .map((l) => (l.code || '').toLowerCase());
      // Set to first available language if current selection is not in the list
      if (!currentLang || !langCodes.includes(currentLang)) {
        const fallback = languages.find((l) => l && l.code)?.code;
        if (fallback) setValue('language', fallback, { shouldDirty: true });
      }
    }
  }, [languages, formValues.language, setValue]);

  useEffect(() => {
    if (categories.length) {
      const currentCat = (formValues.category || '').toUpperCase();
      const catCodes = categories.map((c) => (c.code || '').toUpperCase());
      if (!catCodes.includes(currentCat)) {
        setValue('category', categories[0].code, { shouldDirty: true });
      }
    }
  }, [categories, formValues.category, setValue]);

  useEffect(() => {
    const tplSamples =
      template?.variableSamples ||
      template?.variable_samples ||
      template?.variablesamples ||
      {};
    if (tplSamples && typeof tplSamples === 'object') {
      const normalized = {
        header: tplSamples.header || tplSamples.Header || {},
        body: tplSamples.body || tplSamples.Body || {},
        footer: tplSamples.footer || tplSamples.Footer || {},
      };
      setVariableSamples(normalized);
    }
  }, [template]);

  const isAllowedFile = (type, file) => {
    if (!file) return false;
    if (type === 'image') return file.type.startsWith('image/');
    if (type === 'video') return file.type.startsWith('video/');
    if (type === 'document') {
      return (
        file.type === 'application/pdf' ||
        file.type === 'application/msword' ||
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-powerpoint' ||
        file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
        file.type === 'text/plain'
      );
    }
    return false;
  };

  useEffect(() => {
    setVariableSamples((prev) => {
      const next = { header: {}, body: {}, footer: {} };
      headerVariables.forEach((v) => {
        next.header[v] = prev.header?.[v] || '';
      });
      bodyVariables.forEach((v) => {
        next.body[v] = prev.body?.[v] || '';
      });
      footerVariables.forEach((v) => {
        next.footer[v] = prev.footer?.[v] || '';
      });
      if (
        areSectionSamplesEqual(prev.header, next.header) &&
        areSectionSamplesEqual(prev.body, next.body) &&
        areSectionSamplesEqual(prev.footer, next.footer)
      ) {
        return prev;
      }
      return next;
    });
  }, [formValues.header, formValues.body, formValues.footer]);

  useEffect(() => {
    if (!recentVariable || !recentVariable.section) return;
    const { section, key } = recentVariable;
    const value = variableSamples[section]?.[key];
    if (value === undefined) return;
    const input = sampleInputRefs.current[`${section}-${key}`];
    if (input) {
      setTimeout(() => {
        input.focus();
        input.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 0);
    }
    setRecentVariable(null);
  }, [recentVariable, variableSamples]);

  const handleSampleChange = (section, name, value) => {
    setVariableSamples((prev) => ({
      ...prev,
      [section]: { ...(prev[section] || {}), [name]: value },
    }));
  };

  const handleFormSubmit = async (data) => {
    const counts = getButtonStats(buttonValues);
    if (counts.hasQuick && counts.hasCta) {
      setButtonLimitError('You cannot mix quick reply buttons with URL/phone buttons.');
      return;
    }
    if (counts.hasQuick && counts.total > 3) {
      setButtonLimitError('You can add up to 3 quick reply buttons.');
      return;
    }
    if (counts.hasCta && counts.total > 2) {
      setButtonLimitError('You can add up to 2 call-to-action buttons (URL/phone).');
      return;
    }
    const rawVars = [
      ...extractRawVariables(data.header),
      ...extractRawVariables(data.body),
      ...extractRawVariables(data.footer),
    ];
    const invalidVars = rawVars.filter((v) => !/^\d+$/.test(v));
    if (invalidVars.length > 0) {
      setVariableError('Variables must be numeric like {{1}}, {{2}}.');
      return;
    }
    setVariableError('');

    if (headerUploading) {
      setHeaderUploadError('Header media is still uploading. Please wait.');
      return;
    }

    let headerMediaUrl = data.headerMediaUrl;
    let headerHandle = data.headerHandle;

    // Validate media header selection (upload happens on create request)
    if (data.headerType !== 'none' && data.headerType !== 'text') {
      if (!headerFile && !data.headerHandle) {
        setHeaderUploadError('Please upload a media file for the header.');
        return;
      }
      if (headerPreviewUrl) {
        headerMediaUrl = headerPreviewUrl;
      }
    }

    // Submit form data with headerHandle for Meta API
    onSubmit?.({
      ...data,
      headerMediaUrl,
      headerHandle,
      headerFile,
      variableSamples,
    });
  };

  const handleDraft = () => {
    onSaveDraft?.({ ...formValues, variableSamples });
  };

  const insertVariable = (field, ref) => {
    const currentValue = formValues[field] || '';
    const matches = currentValue.match(/\{\{(\d+)\}\}/g) || [];
    const usedNumbers = matches.map((m) => parseInt(m.replace(/[{}]/g, ''), 10)).filter((n) => !Number.isNaN(n));
    const nextNum = usedNumbers.length ? Math.max(...usedNumbers) + 1 : 1;
    const name = `${nextNum}`;
    const placeholder = `{{${name}}}`;

    const input = ref?.current;
    const selectionStart = input?.selectionStart ?? currentValue.length;
    const selectionEnd = input?.selectionEnd ?? currentValue.length;
    const updated =
      currentValue.slice(0, selectionStart) +
      placeholder +
      currentValue.slice(selectionEnd);
    setValue(field, updated, { shouldValidate: true, shouldDirty: true });

    // Restore cursor after render
    setTimeout(() => {
      if (input) {
        const pos = selectionStart + placeholder.length;
        input.setSelectionRange(pos, pos);
        input.focus();
      }
    }, 0);

    const section = field === 'header' ? 'header' : field === 'footer' ? 'footer' : 'body';
    setRecentVariable({ section, key: name });
  };

  const insertTextAtCursor = (field, ref, text) => {
    const currentValue = formValues[field] || '';
    const input = ref?.current;
    const selectionStart = input?.selectionStart ?? currentValue.length;
    const selectionEnd = input?.selectionEnd ?? currentValue.length;
    const updated =
      currentValue.slice(0, selectionStart) +
      text +
      currentValue.slice(selectionEnd);
    setValue(field, updated, { shouldValidate: true, shouldDirty: true });
    setTimeout(() => {
      if (input) {
        const pos = selectionStart + text.length;
        input.setSelectionRange(pos, pos);
        input.focus();
      }
    }, 0);
  };

  const wrapSelection = (field, ref, prefix, suffix) => {
    const currentValue = formValues[field] || '';
    const input = ref?.current;
    const selectionStart = input?.selectionStart ?? currentValue.length;
    const selectionEnd = input?.selectionEnd ?? currentValue.length;
    const selected = currentValue.slice(selectionStart, selectionEnd);
    const updated =
      currentValue.slice(0, selectionStart) +
      prefix +
      selected +
      suffix +
      currentValue.slice(selectionEnd);
    setValue(field, updated, { shouldValidate: true, shouldDirty: true });
    setTimeout(() => {
      if (input) {
        const pos = selected
          ? selectionEnd + prefix.length + suffix.length
          : selectionStart + prefix.length;
        input.setSelectionRange(pos, pos);
        input.focus();
      }
    }, 0);
  };

  const openEmojiPicker = (event, field) => {
    setEmojiTarget(field);
    setEmojiAnchorEl(event.currentTarget);
  };

  const closeEmojiPicker = () => {
    setEmojiAnchorEl(null);
  };

  const uploadTemplateHeader = async (file) => {
    setHeaderUploading(true);
    setHeaderUploadError('');
    try {
      const response = await uploadApi.uploadTemplateMedia(file);
      const payload = response?.data?.data || response?.data || {};
      const handle =
        payload.header_handle ||
        payload.handle ||
        payload.media_id ||
        payload.id ||
        '';
      if (!handle) {
        throw new Error('Media handle not returned from upload');
      }
      setValue('headerHandle', handle, { shouldValidate: true, shouldDirty: true });
      return handle;
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to upload media';
      setHeaderUploadError(message);
      setValue('headerHandle', '', { shouldValidate: true, shouldDirty: true });
      return '';
    } finally {
      setHeaderUploading(false);
    }
  };

  const handleHeaderFileChange = async (event) => {
    const file = event.target.files?.[0];
    const type = formValues.headerType;
    if (!file || !type || type === 'none' || type === 'text') return;
    if (!isAllowedFile(type, file)) {
      setHeaderUploadError(`Invalid file type for ${type} header`);
      return;
    }

    if (headerPreviewUrl) {
      URL.revokeObjectURL(headerPreviewUrl);
    }

    setHeaderUploadError('');
    setHeaderUploadName(file.name);
    setHeaderFile(file);
    setValue('headerHandle', '', { shouldValidate: true, shouldDirty: true });
    const objectUrl = URL.createObjectURL(file);
    setHeaderPreviewUrl(objectUrl);
    setValue('headerMediaUrl', objectUrl, { shouldValidate: true, shouldDirty: true });

    if (isEdit) {
      await uploadTemplateHeader(file);
    }
  };


  const textLength = (value) => (value ? value.length : 0);

  const getButtonStats = (values = []) => {
    const counts = { total: 0, quick: 0, url: 0, phone: 0, hasQuick: false, hasCta: false };
    (Array.isArray(values) ? values : []).forEach((btn) => {
      const type = btn?.type;
      if (!type) return;
      counts.total += 1;
      if (type === 'QUICK_REPLY') counts.quick += 1;
      if (type === 'URL') counts.url += 1;
      if (type === 'PHONE_NUMBER') counts.phone += 1;
    });
    counts.hasQuick = counts.quick > 0;
    counts.hasCta = counts.url > 0 || counts.phone > 0;
    return counts;
  };

  const canUseType = (type, currentType = null) => {
    const counts = getButtonStats(buttonValues);
    if (currentType) {
      if (currentType === 'QUICK_REPLY') counts.quick -= 1;
      if (currentType === 'URL') counts.url -= 1;
      if (currentType === 'PHONE_NUMBER') counts.phone -= 1;
      counts.total = Math.max(0, counts.total);
    }
    counts.hasQuick = counts.quick > 0;
    counts.hasCta = counts.url > 0 || counts.phone > 0;

    if ((type === 'QUICK_REPLY' && counts.hasCta) || (type !== 'QUICK_REPLY' && counts.hasQuick)) {
      setButtonLimitError('You cannot mix quick reply buttons with URL/phone buttons.');
      return false;
    }
    if (type === 'QUICK_REPLY' && counts.quick >= 3) {
      setButtonLimitError('You can add up to 3 quick reply buttons.');
      return false;
    }
    if (type === 'URL' && counts.url >= 1) {
      setButtonLimitError('You can only add 1 URL button.');
      return false;
    }
    if (type === 'PHONE_NUMBER' && counts.phone >= 1) {
      setButtonLimitError('You can only add 1 phone button.');
      return false;
    }
    if ((type === 'URL' || type === 'PHONE_NUMBER') && counts.url + counts.phone >= 2) {
      setButtonLimitError('You can add up to 2 call-to-action buttons (URL/phone).');
      return false;
    }
    if (type === 'QUICK_REPLY' && counts.total >= 3 && !currentType) {
      setButtonLimitError('You can add up to 3 quick reply buttons.');
      return false;
    }
    setButtonLimitError('');
    return true;
  };

  const handleButtonTypeChange = (index, newType, onChange) => {
    const currentType = buttonFields[index]?.type;
    if (!canUseType(newType, currentType)) return;
    onChange(newType);
    if (newType === 'URL') {
      setValue(`buttons.${index}.urlType`, buttonValues?.[index]?.urlType || 'STATIC', { shouldDirty: true });
      setValue(`buttons.${index}.url`, buttonValues?.[index]?.url || '', { shouldDirty: true });
      setValue(`buttons.${index}.urlVariable`, buttonValues?.[index]?.urlVariable || '', { shouldDirty: true });
    } else {
      setValue(`buttons.${index}.urlType`, 'STATIC', { shouldDirty: true });
      setValue(`buttons.${index}.url`, '', { shouldDirty: true });
      setValue(`buttons.${index}.urlVariable`, '', { shouldDirty: true });
    }

    if (newType === 'PHONE_NUMBER') {
      setValue(`buttons.${index}.phoneCountryCode`, buttonValues?.[index]?.phoneCountryCode || '', { shouldDirty: true });
      setValue(`buttons.${index}.phoneNumber`, buttonValues?.[index]?.phoneNumber || '', { shouldDirty: true });
      loadCountryCodes();
    } else {
      setValue(`buttons.${index}.phoneCountryCode`, '', { shouldDirty: true });
      setValue(`buttons.${index}.phoneNumber`, '', { shouldDirty: true });
    }
  };

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                Template Details
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{
                      required: 'Template name is required',
                      pattern: {
                        value: /^[a-z0-9_]+$/,
                        message: 'Only lowercase letters, numbers, and underscores allowed',
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Template Name"
                        placeholder="e.g., order_confirmation"
                        error={!!errors.name}
                        helperText={
                          errors.name?.message ||
                          'Lowercase letters, numbers, and underscores only'
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="category"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Category</InputLabel>
                        <Select
                          {...field}
                          fullWidth
                          label="Category"
                          displayEmpty
                        >
                          {categories.filter(Boolean).map((cat) => (
                            <MenuItem key={cat.code} value={cat.code}>
                              {cat.name || cat.code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="language"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Language</InputLabel>
                        <Select
                          {...field}
                          fullWidth
                          label="Language"
                          displayEmpty
                        >
                          {languages.filter(Boolean).map((lang) => (
                            <MenuItem key={lang.code} value={lang.code}>
                              {lang.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                Message Content
              </Typography>
              {variableError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {variableError}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="headerType"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Header Type</InputLabel>
                        <Select {...field} label="Header Type">
                          <MenuItem value="none">None</MenuItem>
                          <MenuItem value="text">Text</MenuItem>
                          <MenuItem value="image">Image</MenuItem>
                          <MenuItem value="video">Video</MenuItem>
                          <MenuItem value="document">Document</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  {formValues.headerType === 'text' ? (
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          Header (Text)
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <IconButton size="small" onClick={(event) => openEmojiPicker(event, 'header')}>
                            <Smile size={18} />
                          </IconButton>
                          <Button size="small" variant="text" onClick={() => insertVariable('header', headerInputRef)}>
                            Add Variable
                          </Button>
                        </Stack>
                      </Stack>
                      <Controller
                        name="header"
                        control={control}
                        rules={{
                          validate: (value) =>
                            formValues.headerType !== 'text' || value?.trim()
                              ? true
                              : 'Header text is required',
                        }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Header (Text)"
                            placeholder="Enter header text"
                            error={!!fieldState.error}
                            helperText={
                              fieldState.error?.message ||
                              `Max 60 characters. Use variables like {{1}}. (${textLength(formValues.header)}/60)`
                            }
                            inputProps={{ maxLength: 60 }}
                            inputRef={headerInputRef}
                          />
                        )}
                      />
                    </Stack>
                  ) : formValues.headerType !== 'none' ? (
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Button
                        component="label"
                        variant="outlined"
                        size="small"
                      >
                        {`Choose ${formValues.headerType}`}
                        <input
                          type="file"
                          accept={headerAcceptMap[formValues.headerType] || '*/*'}
                          hidden
                          onChange={handleHeaderFileChange}
                        />
                      </Button>
                      {headerUploadName && (
                        <Typography variant="caption" color="text.secondary">
                          {headerUploadName}
                        </Typography>
                      )}
                      {headerUploading && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={14} />
                          <Typography variant="caption" color="text.secondary">
                            Uploading media...
                          </Typography>
                        </Stack>
                      )}
                    </Stack>

                    {headerUploadError && (
                      <Typography variant="caption" color="error">
                        {headerUploadError}
                      </Typography>
                    )}
                    {isEdit &&
                      formValues.headerType !== 'none' &&
                      formValues.headerType !== 'text' &&
                      !formValues.headerHandle &&
                      !headerFile && (
                        <Typography variant="caption" sx={{ color: 'warning.main' }}>
                          No media handle on file. Re-upload media before submitting.
                        </Typography>
                      )}
                    {formValues.headerMediaUrl && !headerUploadError && (
                      <Typography variant="caption" color="text.secondary">
                        Media selected.
                      </Typography>
                    )}
                    {/* Hidden controllers to keep values for submission */}
                    <Controller
                      name="headerMediaUrl"
                      control={control}
                      rules={{
                        required: 'Media is required for this header type',
                      }}
                      render={({ field }) => (
                        <input type="hidden" {...field} />
                      )}
                    />
                    <Controller
                      name="headerHandle"
                      control={control}
                      render={({ field }) => (
                        <input type="hidden" {...field} />
                      )}
                    />
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No header selected
                  </Typography>
                )}
                </Grid>

                <Grid item xs={12}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          Body
                        </Typography>
                        <Button size="small" variant="text" onClick={() => insertVariable('body', bodyInputRef)}>
                          Add Variable
                        </Button>
                      </Stack>
                  <Controller
                    name="body"
                    control={control}
                    rules={{ required: 'Message body is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Body"
                        placeholder="Enter your message. Use variables like {{1}}, {{2}}"
                        multiline
                        rows={6}
                        error={!!errors.body}
                        helperText={
                          errors.body?.message ||
                          `Use numeric variables like {{1}}. Max 1024 characters. (${textLength(formValues.body)}/1024)`
                        }
                        inputProps={{ maxLength: 1024 }}
                        inputRef={bodyInputRef}
                      />
                    )}
                  />
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap' }}>
                    <IconButton size="small" onClick={(event) => openEmojiPicker(event, 'body')}>
                      <Smile size={18} />
                    </IconButton>
                    <IconButton size="small" onClick={() => wrapSelection('body', bodyInputRef, '*', '*')}>
                      <Bold size={18} />
                    </IconButton>
                    <IconButton size="small" onClick={() => wrapSelection('body', bodyInputRef, '_', '_')}>
                      <Italic size={18} />
                    </IconButton>
                    <IconButton size="small" onClick={() => wrapSelection('body', bodyInputRef, '~', '~')}>
                      <Strikethrough size={18} />
                    </IconButton>
                    <IconButton size="small" onClick={() => wrapSelection('body', bodyInputRef, '`', '`')}>
                      <Code size={18} />
                    </IconButton>
                  </Stack>
                </Grid>

                <Popover
                  open={Boolean(emojiAnchorEl)}
                  anchorEl={emojiAnchorEl}
                  onClose={closeEmojiPicker}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  <Box sx={{ p: 1, display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.5 }}>
                    {emojiList.map((emoji) => (
                      <IconButton
                        key={emoji}
                        size="small"
                        onClick={() => {
                          const targetRef =
                            emojiTarget === 'header'
                              ? headerInputRef
                              : emojiTarget === 'footer'
                                ? footerInputRef
                                : bodyInputRef;
                          insertTextAtCursor(emojiTarget, targetRef, emoji);
                          closeEmojiPicker();
                        }}
                      >
                        <span>{emoji}</span>
                      </IconButton>
                    ))}
                  </Box>
                </Popover>

                <Grid item xs={12}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          Footer (Optional)
                        </Typography>
                        <IconButton size="small" onClick={(event) => openEmojiPicker(event, 'footer')}>
                          <Smile size={18} />
                        </IconButton>
                      </Stack>
                      <Controller
                    name="footer"
                    control={control}
                    render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Footer (Optional)"
                          placeholder="Enter footer text"
                          helperText={`Max 60 characters. (${textLength(formValues.footer)}/60)`}
                          inputProps={{ maxLength: 60 }}
                        inputRef={footerInputRef}
                      />
                    )}
                  />
                </Grid>

              </Grid>
            </CardContent>
          </Card>

          {(headerVariables.length > 0 || bodyVariables.length > 0) && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  Variable Samples
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Add sample content for each variable to help review the template. Do not include real customer data.
                </Typography>
                <Stack spacing={2}>
                  {headerVariables.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Header
                      </Typography>
                      <Stack spacing={1.5}>
                        {headerVariables.map((vKey, idx) => (
                          <Box
                            key={`header-${vKey}`}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                              gap: 1,
                              alignItems: 'center',
                            }}
                          >
                        <TextField
                          size="small"
                          value={`{{${vKey}}}`}
                          label={`Header Variable ${idx + 1}`}
                          InputProps={{ readOnly: true }}
                        />
                            <TextField
                              size="small"
                              label={`Sample for {{${vKey}}}`}
                              placeholder={`Enter content for {{${vKey}}}`}
                              value={variableSamples.header?.[vKey] || ''}
                              onChange={(e) => handleSampleChange('header', vKey, e.target.value)}
                              inputRef={(el) => {
                                if (el) {
                                  sampleInputRefs.current[`header-${vKey}`] = el;
                                }
                              }}
                              helperText="Provide a realistic example (no PII)."
                            />
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {bodyVariables.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Body
                      </Typography>
                      <Stack spacing={1.5}>
                        {bodyVariables.map((vKey, idx) => (
                          <Box
                            key={`body-${vKey}`}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                              gap: 1,
                              alignItems: 'center',
                            }}
                          >
                            <TextField
                              size="small"
                              value={`{{${vKey}}}`}
                              label={`Body Variable ${idx + 1}`}
                              InputProps={{ readOnly: true }}
                            />
                            <TextField
                              size="small"
                              label={`Sample for {{${vKey}}}`}
                              placeholder={`Enter content for {{${vKey}}}`}
                              value={variableSamples.body?.[vKey] || ''}
                              onChange={(e) => handleSampleChange('body', vKey, e.target.value)}
                              inputRef={(el) => {
                                if (el) {
                                  sampleInputRefs.current[`body-${vKey}`] = el;
                                }
                              }}
                              helperText="Provide a realistic example (no PII)."
                            />
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                  {footerVariables.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Footer
                      </Typography>
                      <Stack spacing={1.5}>
                        {footerVariables.map((vKey, idx) => (
                          <Box
                            key={`footer-${vKey}`}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
                              gap: 1,
                              alignItems: 'center',
                            }}
                          >
                            <TextField
                              size="small"
                              value={`{{${vKey}}}`}
                              label={`Footer Variable ${idx + 1}`}
                              InputProps={{ readOnly: true }}
                            />
                            <TextField
                              size="small"
                              label={`Sample for {{${vKey}}}`}
                              placeholder={`Enter content for {{${vKey}}}`}
                              value={variableSamples.footer?.[vKey] || ''}
                              onChange={(e) => handleSampleChange('footer', vKey, e.target.value)}
                              inputRef={(el) => {
                                if (el) {
                                  sampleInputRefs.current[`footer-${vKey}`] = el;
                                }
                              }}
                              helperText="Provide a realistic example (no PII)."
                            />
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Buttons (Optional)
                </Typography>
                <Button
                  size="small"
                  startIcon={<Plus size={18} />}
                  onClick={() => {
                    const counts = getButtonStats(buttonValues);
                    if (counts.hasCta) {
                      setButtonLimitError('You cannot mix quick reply buttons with URL/phone buttons.');
                      return;
                    }
                    if (counts.total >= 3) {
                      setButtonLimitError('You can add up to 3 quick reply buttons.');
                      return;
                    }

                    // Default to a quick reply; user can change type, and per-type guards will apply on change.
                    appendButton({ type: 'QUICK_REPLY', text: '' });
                    setButtonLimitError('');
                  }}
                >
                  Add Button
                </Button>
              </Box>

                {buttonFields.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No buttons added. Choose either up to 3 quick replies or up to 2 call-to-action buttons (1 URL + 1 phone).
                  </Typography>
                ) : (
                buttonFields.map((field, index) => {
                  const currentButton = buttonValues?.[index] || {};
                  const isUrlButton = currentButton.type === 'URL';
                  const isPhoneButton = currentButton.type === 'PHONE_NUMBER';
                  const urlType = currentButton.urlType || 'STATIC';
                  return (
                    <Box
                      key={field.id}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        mb: 2,
                        p: 2,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Controller
                          name={`buttons.${index}.type`}
                          control={control}
                          defaultValue={currentButton.type || 'QUICK_REPLY'}
                          render={({ field }) => (
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                              <InputLabel>Type</InputLabel>
                              <Select
                                {...field}
                                label="Type"
                                onChange={(e) => handleButtonTypeChange(index, e.target.value, field.onChange)}
                              >
                                <MenuItem value="QUICK_REPLY">Quick Reply</MenuItem>
                                <MenuItem value="URL">URL</MenuItem>
                                <MenuItem value="PHONE_NUMBER">Phone</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        />
                        <Controller
                          name={`buttons.${index}.text`}
                          control={control}
                          defaultValue={currentButton.text || ''}
                          rules={{
                            required: 'Button text is required',
                            maxLength: { value: 25, message: 'Max 25 characters' },
                          }}
                          render={({ field, fieldState }) => (
                            <TextField
                              {...field}
                              size="small"
                              label="Button Text"
                              placeholder="Enter button text"
                              sx={{ flex: 1 }}
                              inputProps={{ maxLength: 25 }}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                            />
                          )}
                        />
                        <IconButton onClick={() => removeButton(index)} color="error">
                          <Trash2 size={18} />
                        </IconButton>
                      </Box>

                      {isUrlButton && (
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Controller
                            name={`buttons.${index}.urlType`}
                            control={control}
                            defaultValue={currentButton.urlType || 'STATIC'}
                            render={({ field }) => (
                              <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>URL Type</InputLabel>
                                <Select
                                  {...field}
                                  label="URL Type"
                                  onChange={(e) => field.onChange(e.target.value)}
                                >
                                  <MenuItem value="STATIC">Static</MenuItem>
                                  <MenuItem value="DYNAMIC">Dynamic</MenuItem>
                                </Select>
                              </FormControl>
                            )}
                          />

                          {urlType === 'STATIC' ? (
                            <Controller
                              name={`buttons.${index}.url`}
                              control={control}
                              defaultValue={currentButton.url || ''}
                              rules={{
                                required: 'URL is required',
                                pattern: {
                                  value: /^(https?:\/\/|www\.)/i,
                                  message: 'Enter a valid URL (http/https)',
                                },
                              }}
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...field}
                                  size="small"
                                  label="URL"
                                  placeholder="https://example.com"
                                  fullWidth
                                  error={!!fieldState.error}
                                  helperText={fieldState.error?.message || 'Enter the destination URL'}
                                />
                              )}
                            />
                          ) : (
                            <>
                              <Controller
                                name={`buttons.${index}.url`}
                                control={control}
                                defaultValue={currentButton.url || ''}
                                rules={{
                                  required: 'Base URL is required',
                                  pattern: {
                                    value: /^(https?:\/\/|www\.)/i,
                                    message: 'Enter a valid URL (http/https)',
                                  },
                                  validate: (value) =>
                                    value?.includes('{{1}}') || 'URL must contain {{1}} variable placeholder',
                                }}
                                render={({ field, fieldState }) => (
                                  <TextField
                                    {...field}
                                    size="small"
                                    label="Dynamic URL"
                                    placeholder="https://example.com/track?id={{1}}"
                                    fullWidth
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message || 'Include {{1}} where the dynamic value goes'}
                                  />
                                )}
                              />
                              <Controller
                                name={`buttons.${index}.urlVariable`}
                                control={control}
                                defaultValue={currentButton.urlVariable || ''}
                                rules={{
                                  required: 'Example value is required for dynamic URL',
                                }}
                                render={({ field, fieldState }) => (
                                  <TextField
                                    {...field}
                                    size="small"
                                    label="Example Value"
                                    placeholder="e.g., ORD123456"
                                    fullWidth
                                    helperText={
                                      fieldState.error?.message ||
                                      'Sample value for {{1}} (e.g., order ID)'
                                    }
                                    error={!!fieldState.error}
                                  />
                                )}
                              />
                            </>
                          )}
                        </Box>
                      )}

                      {isPhoneButton && (
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                          <Controller
                            name={`buttons.${index}.phoneCountryCode`}
                            control={control}
                            defaultValue={currentButton.phoneCountryCode || ''}
                            rules={{ required: 'Country code is required' }}
                            render={({ field, fieldState }) => (
                              <FormControl size="small" sx={{ minWidth: 180 }} error={!!fieldState.error}>
                                <InputLabel>Country Code</InputLabel>
                                <Select
                                  {...field}
                                  label="Country Code"
                                  onOpen={loadCountryCodes}
                                  MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                                >
                                  {loadingCountryCodes && (
                                    <MenuItem disabled>Loading...</MenuItem>
                                  )}
                                  {countryCodeError && !loadingCountryCodes && (
                                    <MenuItem disabled>{countryCodeError}</MenuItem>
                                  )}
                                  {!loadingCountryCodes && !countryCodeError && countryCodes.length === 0 && (
                                    <MenuItem disabled>No country codes available</MenuItem>
                                  )}
                                  {countryCodes.map((item) => (
                                    <MenuItem key={item.value} value={item.value}>
                                      {item.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {fieldState.error && (
                                  <Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.5 }}>
                                    {fieldState.error.message}
                                  </Typography>
                                )}
                              </FormControl>
                            )}
                          />

                          <Controller
                            name={`buttons.${index}.phoneNumber`}
                            control={control}
                            defaultValue={currentButton.phoneNumber || ''}
                            rules={{
                              required: 'Phone number is required',
                              minLength: { value: 8, message: 'Minimum 8 digits required' },
                              maxLength: { value: 20, message: 'Maximum 20 digits allowed' },
                              pattern: {
                                value: /^[0-9]+$/,
                                message: 'Digits only',
                              },
                            }}
                            render={({ field, fieldState }) => (
                              <TextField
                                {...field}
                                size="small"
                                label="Phone Number"
                                placeholder="Enter number without country code"
                                inputProps={{ maxLength: 20 }}
                                error={!!fieldState.error}
                                helperText={
                                  fieldState.error?.message ||
                                  'Enter subscriber number (8-20 digits)'
                                }
                              />
                            )}
                          />
                        </Box>
                      )}
                    </Box>
                  );
                })
              )}
              {buttonLimitError && (
                <Typography variant="caption" color="error">
                  {buttonLimitError}
                </Typography>
              )}
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            {onSaveDraft && (
              <Button type="button" variant="outlined" onClick={handleDraft} disabled={loading}>
                Save Draft
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} color="inherit" />}
            >
              {isEdit ? 'Update Template' : 'Create Template'}
            </Button>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="outlined"
            onClick={() => onCancel?.()}
            disabled={loading}
          >
            Cancel
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          width: { xs: '100%', md: 380, lg: 420 },
          position: { xs: 'static', md: 'sticky' },
          top: { md: 20 },
          alignSelf: 'flex-start',
        }}
      >
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Preview
            </Typography>
            <TemplatePreview template={{ ...formValues, variableSamples }} showHeaderMedia />
          </CardContent>
        </Card>

        <Typography variant="body2" color="text.secondary">
          Add a header, body, and footer for your template. Cloud API hosted by Meta will review
          the template variables and content to protect the security and integrity of services.
          Variable parameters must be numeric placeholders wrapped in double curly brackets
          (for example, {"{{1}}"}, {"{{2}}"}).
        </Typography>
      </Box>
    </Stack>
  );
};

export default TemplateForm;
